<script lang="ts">
  import type { RichtextStoryblok, TextCardStoryblok } from "$types/components";
  import {
    storyblokEditable,
    renderRichText,
    type ISbRichtext,
    type SbBlokData,
  } from "@storyblok/svelte";
  import { getAppContext } from "$lib/utils/contexts";
  import { decodeHTML } from "$lib/utils/helpers";
  import { textDir } from "$stores";
  import AppStoreButtons from "$lib/molecules/AppStoreButtons.svelte";
  import GetDeliveryButton from "$lib/consumer/GetDeliveryButton.svelte";

  export let blok:
    | RichtextStoryblok
    | TextCardStoryblok
    | { content: ISbRichtext };

  export let isLight = false;

  const isPostS6 = getAppContext("pageType") === "post-s6";
  const className = "className" in blok ? blok.className : "";
  const align = "textAlignment" in blok ? blok.textAlignment : "left";

  // Check if this is a rich text component
  function isRichtextStoryblok(value: SbBlokData): value is RichtextStoryblok {
    return value.component === "richText";
  }

  // Get the rich text content
  const richTextContent = Array.isArray(blok?.content)
    ? blok.content
    : blok?.content?.content;

  /**
   * Render a rich text node as an HTML string
   * @param node - The rich text node to render
   * @returns - The rendered HTML string
   */
  function renderRichTextNode(node: ISbRichtext | RichtextStoryblok) {
    // Force rendering of empty paragraphs by adding an empty text node
    if (
      node.type === "paragraph" &&
      (!node.content || node.content.length === 0)
    ) {
      node.content = [{ type: "text", text: "" }];
    }

    // Return the rendered node
    return renderRichText({
      type: "doc",
      content: [node as ISbRichtext],
    });
  }
</script>

<div
  class={`
    rich-text
    ${$$restProps.class || ""}
    ${className}
    ${$$restProps.class?.includes("type:") ? "" : "type:body-medium"}
  `}
  class:w-[17%]={isRichtextStoryblok(blok) && blok.desktopWidth === "2/12"}
  class:w-[33%]={isRichtextStoryblok(blok) && blok.desktopWidth === "4/12"}
  class:w-[50%]={isRichtextStoryblok(blok) && blok.desktopWidth === "6/12"}
  class:w-[75%]={isRichtextStoryblok(blok) && blok.desktopWidth === "8/12"}
  class:w-[83%]={isRichtextStoryblok(blok) && blok.desktopWidth === "10/12"}
  class:is-light={isLight}
  class:post-s6={isPostS6}
  use:storyblokEditable={blok}
  dir={$textDir}
  class:text-center={align === "center"}
  class:mx-auto={align === "center"}
  class:text-right={align === "right"}
>
  {#if richTextContent}
    {#each richTextContent as richTextNode}
      {#if richTextNode.type === "blok"}
        {#each richTextNode.attrs.body as blok}
          {#if blok.component === "appStoreButtons"}
            <div class="rich-text-app-store-buttons">
              <AppStoreButtons {blok} />
            </div>
          {:else if blok.component === "getDeliveryButton"}
            <div class="rich-text-get-delivery-button">
              <GetDeliveryButton {blok} />
            </div>
          {/if}
        {/each}
      {:else}
        {@html decodeHTML(renderRichTextNode(richTextNode))}
      {/if}
    {/each}
  {/if}
</div>

<style lang="postcss">
  .is-light {
    color: var(--color-light-grey-rgba);

    :global(a) {
      text-decoration: none;
      position: relative;
      transition: color 0.2s;

      &:after {
        content: "";
        position: absolute;
        bottom: em(0px, 14px);
        left: 0;
        height: 1px;
        width: 100%;
        background: var(--color-light-grey-rgba);
        transition: background 0.2s;
      }

      &:hover {
        color: rgb(var(--color-white));

        &:after {
          background: rgb(var(--color-white));
        }
      }
    }

    :global(strong),
    :global(b) {
      color: rgb(var(--color-white));
    }
  }

  .rich-text {
    :global(strong),
    :global(b) {
      @apply text-charcoal;
    }

    :global(h4) {
      @extend type\:h4;
    }

    :global(p:not(:last-child)) {
      margin-bottom: 18px;
    }

    :global(ul:not(:last-child)) {
      margin-bottom: 18px;
    }

    :global(a) {
      color: currentColor;
    }

    :global(a[data-type="badge"] img) {
      height: 40px;
      display: inline-block;
      margin-right: 10px;
      margin-top: 10px;
    }
  }

  .rich-text-app-store-buttons {
    margin-top: 2rem;
    margin-bottom: 1rem;
  }

  .rich-text-get-delivery-button {
    margin-top: 2rem;
    margin-bottom: 1rem;

    :global(.address-form) {
      border: 2px solid rgb(var(--color-consumer-yellow));
    }
  }

  .post-s6 {
    :global(p:not(:last-child)),
    :global(ul:not(:last-child)) {
      margin-bottom: 0;
    }

    :global(p) {
      margin-top: 24px;
      margin-bottom: 0;
    }

    display: flex;
    flex-direction: column;
  }
</style>
